/* eslint-disable tss-unused-classes/unused-classes -- classes are used via returned object from makeStyles hook */
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { BALANCE_SHEET_COLUMN_GAP_WIDTH, getBalanceSheetPadding } from '../../BalanceSheetTable.layout';
import { BalanceSheetCellPosition, BalanceSheetCellStyle, BalanceSheetDensity, BalanceSheetSummaryVariant } from '../../BalanceSheetTable.types';
import { BALANCE_SHEET_ROW_CLASSNAME } from '../BalanceSheetRow/BalanceSheetRow.styles';

/**
 * Presentation context colors (neutral grayscale for white-labeling)
 */
const PRESENTATION_COLORS = {
  textPrimary: COLORS.NEUTRAL_GRAY[900],
  textSecondary: COLORS.NEUTRAL_GRAY[500],
  textNegative: COLORS.FUNCTIONAL.ERROR.DEFAULT,
  border: COLORS.NEUTRAL_GRAY[700],
  borderLight: COLORS.NEUTRAL_GRAY[300],
  summaryBackground: COLORS.NEUTRAL_GRAY[100],
  summaryBackgroundStrong: COLORS.NEUTRAL_GRAY[200]
};

/**
 * App context colors (navy theme)
 */
const APP_COLORS = {
  textPrimary: COLORS.TEXT.PRIMARY,
  textSecondary: COLORS.TEXT.SECONDARY,
  textNegative: COLORS.FUNCTIONAL.ERROR.DEFAULT,
  border: COLORS.GRAY[500],
  borderLight: COLORS.GRAY[200],
  summaryBackground: COLORS.NAVY[50],
  summaryBackgroundStrong: COLORS.NAVY[100]
};

/**
 * Get border style based on cell style and context.
 * Border widths per Figma design:
 * - default/section-end: 1px light border
 * - section-totals/subtotals-thin: 1px dark border
 * - subtotals-thick/totals: 2px dark border
 */
const getBorderStyle = (cellStyle: BalanceSheetCellStyle, isPresentation: boolean): string => {
  const colors = isPresentation ? PRESENTATION_COLORS : APP_COLORS;
  switch (cellStyle) {
    case 'default-primary':
    case 'default-secondary':
      // Default cells have a light 1px border
      return `1px solid ${colors.borderLight}`;
    case 'section-end-primary':
    case 'section-end-secondary':
      // Section end cells have a 1px dark border
      return `1px solid ${colors.border}`;
    case 'section-totals':
    case 'subtotals-thin':
      // Section totals and thin subtotals have 1px dark border
      return `1px solid ${colors.border}`;
    case 'subtotals-thick':
    case 'totals':
      // Thick subtotals and totals have 2px dark border
      return `2px solid ${colors.border}`;
  }
};

/**
 * Get font weight based on cell style per Figma design.
 * The "-primary" and "-secondary" suffixes determine the font weight:
 * - Totals/subtotals: Bold (600)
 * - Primary styles: Medium (500)
 * - Secondary styles: Regular (400)
 */
const getFontWeight = (cellStyle: BalanceSheetCellStyle): number => {
  switch (cellStyle) {
    case 'section-totals':
    case 'subtotals-thin':
    case 'subtotals-thick':
    case 'totals':
      return FONT_WEIGHTS.bold;
    // 600
    case 'default-secondary':
    case 'section-end-secondary':
      return FONT_WEIGHTS.regular;
    // 400
    default:
      // default-primary, section-end-primary
      return FONT_WEIGHTS.medium;
    // 500
  }
};
export interface BalanceSheetCellStyleProps {
  cellStyle: BalanceSheetCellStyle;
  cellPosition: BalanceSheetCellPosition;
  density: BalanceSheetDensity;
  alignment: 'left' | 'right';
  isPresentation: boolean;
  indentLevel: number;
  isNegative: boolean;
  indentStartContent: boolean;
  minWidth?: number;
  maxWidth?: number;
  summaryVariant?: BalanceSheetSummaryVariant;
  hasExpandIcon: boolean;
}

/**
 * Pixels to add per indent level for nested rows.
 * App mode: 28px per level (to align with text after expand icon)
 * Presentation mode: 8px per level (subtle visual indent)
 */
const getIndentPixelsPerLevel = (isPresentation: boolean): number => {
  return isPresentation ? 8 : 24;
};
export const useBalanceSheetCellStyles = makeStyles<BalanceSheetCellStyleProps>()((_theme: Theme, props) => {
  const {
    cellStyle,
    cellPosition,
    density,
    alignment,
    isPresentation,
    indentLevel,
    isNegative,
    indentStartContent,
    summaryVariant,
    hasExpandIcon
  } = props;
  const colors = isPresentation ? PRESENTATION_COLORS : APP_COLORS;
  const borderStyle = getBorderStyle(cellStyle, isPresentation);
  const fontWeight = getFontWeight(cellStyle);
  // Flat background strokes avoid inset-shadow seams at cell edges in PDFs.
  const totalsDividers = {
    marginTop: 2,
    backgroundImage: `linear-gradient(${colors.border}, ${colors.border}), linear-gradient(${colors.border}, ${colors.border})`,
    backgroundSize: '100% 2px',
    backgroundPosition: 'top, bottom',
    backgroundRepeat: 'no-repeat'
  };

  // Determine horizontal padding based on position.
  // In app mode, category rows have an expand icon (16px + 8px gap = 24px)
  // that pushes text right. Non-expand rows at indent level 0 need matching
  // padding so "Subtotals", "Total net worth", etc. align with category text.
  const getIndentPadding = () => {
    if (cellPosition !== 'start') return 0;
    if (indentLevel > 0) {
      return indentLevel * getIndentPixelsPerLevel(isPresentation);
    }
    if (!isPresentation && !hasExpandIcon && indentStartContent) {
      return 24;
    }
    return 0;
  };
  const padding = getBalanceSheetPadding(density, cellPosition, {
    indentLeft: getIndentPadding()
  });

  /**
   * Determine background color based on cell position and summary variant.
   * Summary cells get a background color; the variant controls intensity.
   */
  const getBackgroundColor = () => {
    if (cellPosition === 'summary') {
      if (summaryVariant === 'blank') {
        return 'transparent';
      }
      if (summaryVariant === 'strong') {
        return colors.summaryBackgroundStrong;
      }
      return colors.summaryBackground;
    }
    return 'transparent';
  };

  /**
   * Determine text color based on cell style per Figma design.
   * The "-secondary" suffix indicates secondary (gray) text color.
   * Negative values use the same color as positive values — the
   * accounting parentheses notation is sufficient to indicate negative.
   * In presentation mode, negatives are shown in red for emphasis.
   */
  const getTextColor = () => {
    if (isNegative && isPresentation) {
      return colors.textNegative;
    }
    // Secondary styles use secondary text color (gray)
    if (cellStyle === 'default-secondary' || cellStyle === 'section-end-secondary') {
      return colors.textSecondary;
    }
    return colors.textPrimary;
  };
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: alignment === 'right' ? 'flex-end' : 'flex-start',
      // Grid sizes this cell via the column track. width: 100% ensures the
      // cell fills its parent when nested inside a wrapper (e.g. the asset
      // column's position:relative Box). minWidth: 0 allows content to
      // shrink below its intrinsic size so text truncates.
      width: '100%',
      minWidth: 0,
      ...(cellStyle !== 'totals' && {
        borderBottom: borderStyle
      }),
      // Leave a 2px gap after Subtotals, then draw both Totals strokes
      // inside the cell so the row keeps its existing height.
      ...(cellStyle === 'totals' && totalsDividers),
      backgroundColor: getBackgroundColor(),
      // Show hover background when the parent row is hovered. Only applies
      // to cells with a transparent background so summary columns keep
      // their distinct coloring.
      ...(getBackgroundColor() === 'transparent' && {
        [`.${BALANCE_SHEET_ROW_CLASSNAME}:hover &`]: {
          backgroundColor: COLORS.FUNCTIONAL.HOVER
        }
      }),
      ...padding
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: '100%',
      minWidth: 0,
      alignItems: alignment === 'right' ? 'flex-end' : 'flex-start'
    },
    primaryContent: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      minWidth: 0,
      justifyContent: alignment === 'right' ? 'flex-end' : 'flex-start'
    },
    lineOne: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight,
      color: getTextColor(),
      textAlign: alignment,
      // Only truncate text in start position (asset names); data columns should not truncate
      ...(cellPosition === 'start' ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      } : {
        whiteSpace: 'nowrap'
      })
    },
    lineTwo: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHTS.regular,
      color: colors.textSecondary,
      textAlign: alignment,
      // The expand icon (16px wide + 8px gap) pushes lineOne right, so match
      // that offset to keep the secondary text aligned with the label.
      ...(cellPosition === 'start' && hasExpandIcon && !isPresentation ? {
        paddingLeft: 24
      } : {}),
      // Only truncate text in start position; data columns should not truncate
      ...(cellPosition === 'start' ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      } : {
        whiteSpace: 'nowrap'
      })
    },
    expandIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      flexShrink: 0,
      cursor: 'pointer',
      color: colors.textPrimary,
      transition: 'transform 0.2s ease-in-out'
    },
    expandIconExpanded: {
      transform: 'rotate(90deg)'
    },
    columnGap: {
      width: BALANCE_SHEET_COLUMN_GAP_WIDTH,
      flexShrink: 0,
      alignSelf: 'stretch',
      // Continue row dividers through the gap between summary sections.
      ...(cellStyle === 'subtotals-thick' && {
        borderBottom: borderStyle
      }),
      ...(cellStyle === 'totals' && totalsDividers)
    }
  };
});